import axios from "axios";
import React from "react";
import { getRequestHeaders } from "../api";
import { baseUrl } from "../assets/Animations";
import FinancialsActualsLineChart from "./FinancialsActualsLineChart";
import "./financials.css";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const schoolList = ["mma", "lsma", "tma", "mor", "psma", "tem"];

const schoolLabels = {
  mma: "Mountainside",
  lsma: "Lakeside",
  tma: "Treeside",
  mor: "Morningside",
  psma: "Parkside",
  tem: "Tempe",
  Unknown: "Unknown",
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("en-US");

const resolveMonthYearFromTimesheet = (timesheet) => {
  if (!timesheet) return null;
  if (timesheet.monthNumber && timesheet.year) {
    return { month: Number(timesheet.monthNumber), year: Number(timesheet.year) };
  }
  if (timesheet.monthYear && String(timesheet.monthYear).includes("/")) {
    const [m, y] = String(timesheet.monthYear).split("/");
    return { month: Number(m), year: Number(y) };
  }
  if (timesheet.month && timesheet.year) {
    const monthIndex = monthNames.findIndex(
      (name) => name.toLowerCase() === String(timesheet.month).toLowerCase()
    );
    if (monthIndex >= 0) {
      return { month: monthIndex + 1, year: Number(timesheet.year) };
    }
  }
  return null;
};

const resolveMonthYearFromInvoice = (invoice) => {
  if (!invoice) return null;
  if (invoice.monthNumber && invoice.year) {
    return { month: Number(invoice.monthNumber), year: Number(invoice.year) };
  }
  if (invoice.monthYear && String(invoice.monthYear).includes("/")) {
    const [m, y] = String(invoice.monthYear).split("/");
    return { month: Number(m), year: Number(y) };
  }
  return null;
};

const findFamilyForInvoice = (invoice, students) => {
  if (!invoice?.waveCustomerId) return null;
  return students.find((student) => student.waveCustomerId === invoice.waveCustomerId) || null;
};

const findStudentRate = (invoice, lesson, nameOnInvoice, lengthOfLesson) => {
  let found = false;
  const parsedLength = parseInt(lengthOfLesson, 10);

  if (
    parsedLength === 31 ||
    parsedLength === 46 ||
    parsedLength === 61 ||
    parsedLength === 32 ||
    parsedLength === 47 ||
    parsedLength === 62
  ) {
    found = true;
    if (parsedLength === 31) return 25;
    if (parsedLength === 46) return 35;
    if (parsedLength === 61) return 45;
    if (parsedLength === 32 || parsedLength === 47 || parsedLength === 62) return 0;
  } else if (lesson?.studentAllRates) {
    return Number(lesson.studentAllRates[lengthOfLesson] || 0);
  } else if (invoice?.familyInfo?.students?.length) {
    for (let i = 0; i < invoice.familyInfo.students.length; i++) {
      if (invoice.familyInfo.students[i].studentName === nameOnInvoice) {
        found = true;
        if (!invoice.familyInfo.students[i].allRates) {
          return Number(invoice.familyInfo.students[i].lessonRate || 0);
        }
        return Number(invoice.familyInfo.students[i].allRates[lengthOfLesson] || 0);
      }
    }
  }

  if (!found) return 0;
  return 0;
};

const calculateEstimatedPayAgain = (theTimesheet) => {
  let minutesTaught = 0;
  let allExtras = 0;
  const families = Array.isArray(theTimesheet?.families) ? theTimesheet.families : [];

  for (let i = 0; i < families.length; i++) {
    const fam = families[i];
    for (let j = 0; j < fam.lessons.length; j++) {
      minutesTaught = Number(minutesTaught) + Number(fam.lessons[j].lessonLength || 0);
    }
    for (let j = 0; j < fam.cancellations.length; j++) {
      if (fam.cancellations[j].cancellor === "student less than 24hrs") {
        minutesTaught = Number(minutesTaught) + Number(fam.cancellations[j].lessonLength || 0);
      }
    }
    for (let j = 0; j < fam.extras.length; j++) {
      allExtras = Number(allExtras) + Number(fam.extras[j].amount || 0);
    }
  }

  return (minutesTaught / 60) * Number(theTimesheet.teacherRate || 0) + allExtras;
};

const getLessonStats = (monthlyTimesheets) => {
  let totalLessonsGiven = 0;
  let totalMinutesOfLessons = 0;
  let studentsTaught = 0;
  const studentHash = {};

  monthlyTimesheets.forEach((sheet) => {
    const families = Array.isArray(sheet?.families) ? sheet.families : [];
    families.forEach((family) => {
      const lessons = Array.isArray(family?.lessons) ? family.lessons : [];
      lessons.forEach((lesson) => {
        totalLessonsGiven += 1;
        totalMinutesOfLessons += Number(lesson.lessonLength || 0);
        if (lesson.studentName && !studentHash[lesson.studentName]) {
          studentHash[lesson.studentName] = true;
          studentsTaught += 1;
        }
      });
    });
  });

  return {
    totalLessonsGiven,
    totalHoursOfLessonsGiven: Math.round((totalMinutesOfLessons / 60) * 100) / 100,
    studentsTaught,
    averageLessonsPerStudent: studentsTaught
      ? Math.round((totalLessonsGiven / studentsTaught) * 100) / 100
      : 0,
  };
};

const buildActualSummary = (entry) => {
  const totalToChargeBySchool = {};
  const totalPayoutBySchool = {};

  schoolList.forEach((school) => {
    totalToChargeBySchool[school] = 0;
    totalPayoutBySchool[school] = 0;
  });

  entry.invoices.forEach((invoice) => {
    if (!invoice?.waveCustomerId || invoice.status === "Scholarship") return;
    const school = invoice.school;
    if (!school || !Object.prototype.hasOwnProperty.call(totalToChargeBySchool, school)) return;

    const lessons = Array.isArray(invoice.lessonsToBeBilled) ? invoice.lessonsToBeBilled : [];
    const extras = Array.isArray(invoice.extrasToBeBilled) ? invoice.extrasToBeBilled : [];

    lessons.forEach((lesson) => {
      totalToChargeBySchool[school] += Number(
        findStudentRate(invoice, lesson, lesson.studentName, lesson.lessonLength) || 0
      );
    });

    extras.forEach((extra) => {
      totalToChargeBySchool[school] += Number(extra.amount || 0);
    });
  });

  entry.timesheets.forEach((sheet) => {
    const school = sheet.school;
    if (!school || !Object.prototype.hasOwnProperty.call(totalPayoutBySchool, school)) return;
    totalPayoutBySchool[school] += Math.floor(Number(calculateEstimatedPayAgain(sheet) || 0));
  });

  const totalRevenue = schoolList.reduce(
    (sum, school) => sum + Number(totalToChargeBySchool[school] || 0),
    0
  );
  const totalCogs = schoolList.reduce(
    (sum, school) => sum + Number(totalPayoutBySchool[school] || 0),
    0
  );
  const lessonStats = getLessonStats(entry.timesheets);

  const bySchool = schoolList.map((school) => ({
    school,
    revenue: Math.floor(Number(totalToChargeBySchool[school] || 0)),
    cogs: Math.floor(Number(totalPayoutBySchool[school] || 0)),
    profit: Math.floor(
      Number(totalToChargeBySchool[school] || 0) - Number(totalPayoutBySchool[school] || 0)
    ),
  }));

  return {
    ...entry,
    revenue: Math.floor(totalRevenue),
    cogs: Math.floor(totalCogs),
    profit: Math.floor(totalRevenue - totalCogs),
    bySchool,
    lessonCount: lessonStats.totalLessonsGiven,
    totalHoursOfLessonsGiven: lessonStats.totalHoursOfLessonsGiven,
    studentsTaught: lessonStats.studentsTaught,
    averageLessonsPerStudent: lessonStats.averageLessonsPerStudent,
  };
};

const Financials = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = React.useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = React.useState(now.getFullYear());
  const [viewMode, setViewMode] = React.useState("projection");
  const [historyViewMode, setHistoryViewMode] = React.useState("table");
  const [financialData, setFinancialData] = React.useState(null);
  const [projectionLoading, setProjectionLoading] = React.useState(true);
  const [projectionError, setProjectionError] = React.useState("");
  const [actualsLoading, setActualsLoading] = React.useState(true);
  const [actualsError, setActualsError] = React.useState("");
  const [timesheets, setTimesheets] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [students, setStudents] = React.useState([]);

  const yearOptions = React.useMemo(() => {
    const currentYear = now.getFullYear();
    return Array.from({ length: 8 }, (_, index) => currentYear - index);
  }, [now]);

  React.useEffect(() => {
    let isMounted = true;
    setProjectionLoading(true);
    setProjectionError("");

    axios
      .get(`${baseUrl}/billing/financials/monthly-projection`, {
        ...getRequestHeaders(),
        params: {
          month: selectedMonth,
          year: selectedYear,
        },
      })
      .then((res) => {
        if (!isMounted) return;
        setFinancialData(res.data || null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.log(err);
        setProjectionError("Unable to load monthly financials.");
      })
      .finally(() => {
        if (!isMounted) return;
        setProjectionLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedMonth, selectedYear]);

  React.useEffect(() => {
    let isMounted = true;
    setActualsLoading(true);
    setActualsError("");

    Promise.all([
      axios.get(`${baseUrl}/getalltimesheetsendpoint`, getRequestHeaders()),
      axios.get(`${baseUrl}/allinvoices`, getRequestHeaders()),
      axios.get(`${baseUrl}/school-management`, getRequestHeaders()),
    ])
      .then(([timesheetsRes, invoicesRes, studentsRes]) => {
        if (!isMounted) return;
        setTimesheets(Array.isArray(timesheetsRes.data) ? timesheetsRes.data : []);
        setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
        setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.log(err);
        setActualsError("Unable to load actual billing data.");
      })
      .finally(() => {
        if (!isMounted) return;
        setActualsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const schoolRows = React.useMemo(() => {
    return Array.isArray(financialData?.bySchool)
      ? [...financialData.bySchool].sort((a, b) => Number(b.profit || 0) - Number(a.profit || 0))
      : [];
  }, [financialData]);

  const actualMonthlyRows = React.useMemo(() => {
    const monthMap = new Map();

    const ensureEntry = (month, year) => {
      const key = `${year}-${month}`;
      if (!monthMap.has(key)) {
        monthMap.set(key, {
          key,
          month,
          year,
          timesheets: [],
          invoices: [],
        });
      }
      return monthMap.get(key);
    };

    timesheets.forEach((sheet) => {
      const resolved = resolveMonthYearFromTimesheet(sheet);
      if (!resolved) return;
      ensureEntry(resolved.month, resolved.year).timesheets.push(sheet);
    });

    invoices.forEach((invoice) => {
      const resolved = resolveMonthYearFromInvoice(invoice);
      if (!resolved) return;
      const entry = ensureEntry(resolved.month, resolved.year);
      const familyInfo = findFamilyForInvoice(invoice, students);
      entry.invoices.push({ ...invoice, familyInfo });
    });

    return Array.from(monthMap.values())
      .map((entry) => buildActualSummary(entry))
      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [invoices, students, timesheets]);

  const selectedActualMonth = React.useMemo(() => {
    return (
      actualMonthlyRows.find(
        (row) => Number(row.month) === Number(selectedMonth) && Number(row.year) === Number(selectedYear)
      ) || null
    );
  }, [actualMonthlyRows, selectedMonth, selectedYear]);

  const actualSchoolRows = React.useMemo(() => {
    return Array.isArray(selectedActualMonth?.bySchool)
      ? [...selectedActualMonth.bySchool].sort((a, b) => Number(b.profit || 0) - Number(a.profit || 0))
      : [];
  }, [selectedActualMonth]);

  const monthColumnHeaders = React.useMemo(() => {
    const byMonth = new Map();

    actualMonthlyRows.forEach((entry) => {
      const monthKey = entry.month;
      if (!byMonth.has(monthKey)) byMonth.set(monthKey, []);
      byMonth.get(monthKey).push(entry);
    });

    const headers = [];
    monthNames.forEach((name, index) => {
      const monthKey = index + 1;
      const entries = byMonth.get(monthKey) || [];
      entries.sort((a, b) => a.year - b.year);
      entries.forEach((entry) => {
        headers.push({
          key: entry.key,
          label: `${name.slice(0, 3)} ${entry.year}`,
          monthIndex: monthKey,
        });
      });
    });

    return headers;
  }, [actualMonthlyRows]);

  const monthGroupStartKeys = React.useMemo(() => {
    const starts = new Set();
    monthColumnHeaders.forEach((header, index) => {
      if (index === 0) {
        starts.add(header.key);
        return;
      }

      const prev = monthColumnHeaders[index - 1];
      if (prev.monthIndex !== header.monthIndex) {
        starts.add(header.key);
      }
    });
    return starts;
  }, [monthColumnHeaders]);

  const actualSummaryByKey = React.useMemo(() => {
    const map = new Map();
    actualMonthlyRows.forEach((entry) => map.set(entry.key, entry));
    return map;
  }, [actualMonthlyRows]);

  const titleLabel = `${monthNames[selectedMonth - 1]} ${selectedYear}`;

  const topError = viewMode === "projection" ? projectionError : actualsError;
  const isLoading = viewMode === "projection" ? projectionLoading : actualsLoading;

  return (
    <div className="financials-page">
      <div className="financials-page__top">
        <div>
          <h1 className="financials-page__title">Financials</h1>
          <p className="financials-page__subtitle">
            Compare projected monthly lesson revenue against finalized billing data from timesheets and invoices.
          </p>
        </div>
        <div className="financials-page__filters">
          <div className="financials-page__view-toggle">
            <button
              className={`financials-page__view-button ${
                viewMode === "projection" ? "financials-page__view-button--active" : ""
              }`}
              onClick={() => setViewMode("projection")}
            >
              Projection
            </button>
            <button
              className={`financials-page__view-button ${
                viewMode === "actuals" ? "financials-page__view-button--active" : ""
              }`}
              onClick={() => setViewMode("actuals")}
            >
              Actuals
            </button>
          </div>
          <select
            className="financials-page__select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {monthNames.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
          <select
            className="financials-page__select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="financials-page__state-card">
          {viewMode === "projection" ? "Loading projected financials..." : "Loading actual financials..."}
        </div>
      ) : topError ? (
        <div className="financials-page__state-card financials-page__state-card--error">
          {topError}
        </div>
      ) : (
        <>
          <div className="financials-page__summary-label">
            {viewMode === "projection" ? `Projection • ${titleLabel}` : `Actuals • ${titleLabel}`}
          </div>

          {viewMode === "projection" ? (
            <>
              <div className="financials-page__kpi-grid">
                <div className="financials-page__kpi-card">
                  <div className="financials-page__kpi-title">Projected Revenue</div>
                  <div className="financials-page__kpi-value">
                    {currencyFormatter.format(Number(financialData?.revenue || 0))}
                  </div>
                </div>
                <div className="financials-page__kpi-card">
                  <div className="financials-page__kpi-title">Projected COGS</div>
                  <div className="financials-page__kpi-value">
                    {currencyFormatter.format(Number(financialData?.cogs || 0))}
                  </div>
                </div>
                <div className="financials-page__kpi-card financials-page__kpi-card--profit">
                  <div className="financials-page__kpi-title">Projected Profit</div>
                  <div className="financials-page__kpi-value">
                    {currencyFormatter.format(Number(financialData?.profit || 0))}
                  </div>
                </div>
              </div>

              <div className="financials-page__secondary-grid">
                <div className="financials-page__secondary-card">
                  <div className="financials-page__secondary-title">Lesson Events</div>
                  <div className="financials-page__secondary-value">
                    {numberFormatter.format(Number(financialData?.lessonCount || 0))}
                  </div>
                </div>
                <div className="financials-page__secondary-card">
                  <div className="financials-page__secondary-title">Charged &lt;24hr Cancellations</div>
                  <div className="financials-page__secondary-value">
                    {numberFormatter.format(Number(financialData?.chargedCancellationCount || 0))}
                  </div>
                </div>
              </div>

              <div className="financials-page__table-card">
                <div className="financials-page__table-header">
                  <h2 className="financials-page__table-title">By School</h2>
                </div>
                <div className="financials-page__table-wrapper">
                  <table className="financials-page__table">
                    <thead>
                      <tr>
                        <th>School</th>
                        <th>Lessons</th>
                        <th>Late Cancels</th>
                        <th>Revenue</th>
                        <th>COGS</th>
                        <th>Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schoolRows.length > 0 ? (
                        schoolRows.map((row) => (
                          <tr key={row.school}>
                            <td>{schoolLabels[row.school] || row.school}</td>
                            <td>{numberFormatter.format(Number(row.lessonCount || 0))}</td>
                            <td>
                              {numberFormatter.format(Number(row.chargedCancellationCount || 0))}
                            </td>
                            <td>{currencyFormatter.format(Number(row.revenue || 0))}</td>
                            <td>{currencyFormatter.format(Number(row.cogs || 0))}</td>
                            <td>{currencyFormatter.format(Number(row.profit || 0))}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="financials-page__empty-cell">
                            No school data for this month.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="financials-page__kpi-grid">
                <div className="financials-page__kpi-card">
                  <div className="financials-page__kpi-title">Actual Revenue</div>
                  <div className="financials-page__kpi-value">
                    {currencyFormatter.format(Number(selectedActualMonth?.revenue || 0))}
                  </div>
                </div>
                <div className="financials-page__kpi-card">
                  <div className="financials-page__kpi-title">Actual COGS</div>
                  <div className="financials-page__kpi-value">
                    {currencyFormatter.format(Number(selectedActualMonth?.cogs || 0))}
                  </div>
                </div>
                <div className="financials-page__kpi-card financials-page__kpi-card--profit">
                  <div className="financials-page__kpi-title">Actual Profit</div>
                  <div className="financials-page__kpi-value">
                    {currencyFormatter.format(Number(selectedActualMonth?.profit || 0))}
                  </div>
                </div>
              </div>

              <div className="financials-page__secondary-grid financials-page__secondary-grid--three-up">
                <div className="financials-page__secondary-card">
                  <div className="financials-page__secondary-title">Lessons Taught</div>
                  <div className="financials-page__secondary-value">
                    {numberFormatter.format(Number(selectedActualMonth?.lessonCount || 0))}
                  </div>
                </div>
                <div className="financials-page__secondary-card">
                  <div className="financials-page__secondary-title">Hours Taught</div>
                  <div className="financials-page__secondary-value">
                    {numberFormatter.format(Number(selectedActualMonth?.totalHoursOfLessonsGiven || 0))}
                  </div>
                </div>
                <div className="financials-page__secondary-card">
                  <div className="financials-page__secondary-title">Students Taught</div>
                  <div className="financials-page__secondary-value">
                    {numberFormatter.format(Number(selectedActualMonth?.studentsTaught || 0))}
                  </div>
                </div>
              </div>

              <div className="financials-page__table-card">
                <div className="financials-page__table-header">
                  <h2 className="financials-page__table-title">Actuals By School</h2>
                </div>
                <div className="financials-page__table-wrapper">
                  <table className="financials-page__table">
                    <thead>
                      <tr>
                        <th>School</th>
                        <th>Revenue</th>
                        <th>COGS</th>
                        <th>Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actualSchoolRows.length > 0 ? (
                        actualSchoolRows.map((row) => (
                          <tr key={row.school}>
                            <td>{schoolLabels[row.school] || row.school}</td>
                            <td>{currencyFormatter.format(Number(row.revenue || 0))}</td>
                            <td>{currencyFormatter.format(Number(row.cogs || 0))}</td>
                            <td>{currencyFormatter.format(Number(row.profit || 0))}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="financials-page__empty-cell">
                            No actuals found for this month.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="financials-page__table-card">
                <div className="financials-page__table-header">
                  <h2 className="financials-page__table-title">Monthly Actuals History</h2>
                  <div className="financials-page__view-toggle">
                    <button
                      className={`financials-page__view-button ${
                        historyViewMode === "table" ? "financials-page__view-button--active" : ""
                      }`}
                      onClick={() => setHistoryViewMode("table")}
                    >
                      Table
                    </button>
                    <button
                      className={`financials-page__view-button ${
                        historyViewMode === "chart" ? "financials-page__view-button--active" : ""
                      }`}
                      onClick={() => setHistoryViewMode("chart")}
                    >
                      Chart
                    </button>
                  </div>
                </div>
                {historyViewMode === "chart" ? (
                  <FinancialsActualsLineChart data={actualMonthlyRows} />
                ) : (
                  <div className="financials-page__table-wrapper">
                    <table className="financials-page__table">
                      <thead>
                        <tr>
                          <th className="financials-page__sticky-col">Metric</th>
                          {monthColumnHeaders.map((header) => (
                            <th
                              key={header.key}
                              className={
                                monthGroupStartKeys.has(header.key)
                                  ? "financials-page__group-start"
                                  : ""
                              }
                            >
                              {header.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {monthColumnHeaders.length > 0 ? (
                          <>
                            <tr>
                              <td className="financials-page__sticky-col">Revenue</td>
                              {monthColumnHeaders.map((header) => {
                                const row = actualSummaryByKey.get(header.key);
                                return (
                                  <td
                                    key={`revenue-${header.key}`}
                                    className={
                                      monthGroupStartKeys.has(header.key)
                                        ? "financials-page__group-start"
                                        : ""
                                    }
                                  >
                                    {currencyFormatter.format(Number(row?.revenue || 0))}
                                  </td>
                                );
                              })}
                            </tr>
                            <tr>
                              <td className="financials-page__sticky-col">COGS</td>
                              {monthColumnHeaders.map((header) => {
                                const row = actualSummaryByKey.get(header.key);
                                return (
                                  <td
                                    key={`cogs-${header.key}`}
                                    className={
                                      monthGroupStartKeys.has(header.key)
                                        ? "financials-page__group-start"
                                        : ""
                                    }
                                  >
                                    {currencyFormatter.format(Number(row?.cogs || 0))}
                                  </td>
                                );
                              })}
                            </tr>
                            <tr>
                              <td className="financials-page__sticky-col">Profit</td>
                              {monthColumnHeaders.map((header) => {
                                const row = actualSummaryByKey.get(header.key);
                                return (
                                  <td
                                    key={`profit-${header.key}`}
                                    className={
                                      monthGroupStartKeys.has(header.key)
                                        ? "financials-page__group-start"
                                        : ""
                                    }
                                  >
                                    {currencyFormatter.format(Number(row?.profit || 0))}
                                  </td>
                                );
                              })}
                            </tr>
                            <tr>
                              <td className="financials-page__sticky-col">Lessons</td>
                              {monthColumnHeaders.map((header) => {
                                const row = actualSummaryByKey.get(header.key);
                                return (
                                  <td
                                    key={`lessons-${header.key}`}
                                    className={
                                      monthGroupStartKeys.has(header.key)
                                        ? "financials-page__group-start"
                                        : ""
                                    }
                                  >
                                    {numberFormatter.format(Number(row?.lessonCount || 0))}
                                  </td>
                                );
                              })}
                            </tr>
                            <tr>
                              <td className="financials-page__sticky-col">Hours</td>
                              {monthColumnHeaders.map((header) => {
                                const row = actualSummaryByKey.get(header.key);
                                return (
                                  <td
                                    key={`hours-${header.key}`}
                                    className={
                                      monthGroupStartKeys.has(header.key)
                                        ? "financials-page__group-start"
                                        : ""
                                    }
                                  >
                                    {numberFormatter.format(Number(row?.totalHoursOfLessonsGiven || 0))}
                                  </td>
                                );
                              })}
                            </tr>
                            <tr>
                              <td className="financials-page__sticky-col">Students</td>
                              {monthColumnHeaders.map((header) => {
                                const row = actualSummaryByKey.get(header.key);
                                return (
                                  <td
                                    key={`students-${header.key}`}
                                    className={
                                      monthGroupStartKeys.has(header.key)
                                        ? "financials-page__group-start"
                                        : ""
                                    }
                                  >
                                    {numberFormatter.format(Number(row?.studentsTaught || 0))}
                                  </td>
                                );
                              })}
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="7" className="financials-page__empty-cell">
                              No actual billing history found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Financials;
